import type { MapNode, ResearchMap } from '../api/types';

export interface MapView {
  nodes: MapNode[];
  /** Entries in the saved structure that could not be read; never silently dropped. */
  unreadable: number;
  origin: 'structure' | 'originalTree' | 'none';
  rootVisible?: boolean;
  groupingFrameLabels: string[];
  hiddenContent?: string;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

function normalise(value: unknown, counter: { unreadable: number }): MapNode[] {
  if (!Array.isArray(value)) return [];
  const nodes: MapNode[] = [];
  for (const entry of value) {
    const raw = asRecord(entry);
    if (raw === null) {
      counter.unreadable += 1;
      continue;
    }
    const badge = raw.displayed_badge;
    nodes.push({
      // A missing or non-text label is shown as unlabeled, never guessed.
      label: typeof raw.label === 'string' ? raw.label : null,
      children: normalise(raw.children, counter),
      ...(typeof raw.children_visibility === 'string' ? { children_visibility: raw.children_visibility } : {}),
      ...(typeof badge === 'string' || typeof badge === 'number' ? { displayed_badge: String(badge) } : {}),
      ...(typeof raw.visibility_note === 'string' ? { visibility_note: raw.visibility_note } : {}),
    });
  }
  return nodes;
}

/**
 * Reads a map's transcribed hierarchy from the canonical `structure.nodes`
 * saved with the seed, falling back to a legacy array structure or
 * `originalTree`. Structural metadata on `structure` wins over top-level copies.
 */
export function mapView(map: ResearchMap): MapView {
  const structure = asRecord(map.structure);
  const candidates: [MapView['origin'], unknown][] = [
    ['structure', structure !== null ? structure.nodes : map.structure],
    ['originalTree', map.originalTree],
  ];
  let nodes: MapNode[] = [];
  let origin: MapView['origin'] = 'none';
  let unreadable = 0;
  for (const [name, value] of candidates) {
    if (!Array.isArray(value) || value.length === 0) continue;
    const counter = { unreadable: 0 };
    const parsed = normalise(value, counter);
    if (parsed.length > 0) {
      nodes = parsed;
      origin = name;
      unreadable = counter.unreadable;
      break;
    }
    unreadable += counter.unreadable;
  }
  const frames = structure?.groupingFrameLabels;
  return {
    nodes,
    unreadable,
    origin,
    rootVisible: typeof structure?.rootVisible === 'boolean' ? structure.rootVisible : map.rootVisible,
    groupingFrameLabels: Array.isArray(frames)
      ? frames.filter((label): label is string => typeof label === 'string')
      : (map.groupingFrameLabels ?? []),
    hiddenContent: typeof structure?.hiddenContent === 'string' ? structure.hiddenContent : map.hiddenContent,
  };
}
